const SCRIPT_TAG_PATTERN = /<\/?(sup|sub)>/gi;
const DEFAULT_FONT_SIZE = 90;
const SCRIPT_FONT_SCALE = 0.6;
const SUPERSCRIPT_OFFSET_FACTOR = -0.35;
const SUBSCRIPT_OFFSET_FACTOR = 0.25;

function getFontSize(font) {
  if (typeof font !== "string") {
    return DEFAULT_FONT_SIZE;
  }

  const pxMatch = font.match(/(\d+(?:\.\d+)?)px/i);
  if (pxMatch) {
    return Number(pxMatch[1]);
  }

  const fallbackMatch = font.match(/\d+(?:\.\d+)?/);
  return fallbackMatch ? Number(fallbackMatch[0]) : DEFAULT_FONT_SIZE;
}

function scaleFont(font, scale = 1) {
  const fontSize = getFontSize(font);
  const scaledSize = Math.max(1, Number((fontSize * scale).toFixed(2)));

  if (typeof font !== "string") {
    return `${scaledSize}px Sans-Serif, Arial`;
  }

  if (/(\d+(?:\.\d+)?)px/i.test(font)) {
    return font.replace(/(\d+(?:\.\d+)?)px/i, `${scaledSize}px`);
  }

  return `${scaledSize}px ${font}`;
}

function getFontFamily(font) {
  if (typeof font !== "string") {
    return "Sans-Serif, Arial";
  }

  const family = font.replace(/^.*?\d+(?:\.\d+)?px\s*/i, "").trim();
  return family || "Sans-Serif, Arial";
}

function applyFontOverrides(font, segment = {}) {
  const fontSize = Number(segment.fontSize);
  const hasFontSize = Number.isFinite(fontSize) && fontSize > 0;
  const fontFamily = typeof segment.fontFamily === "string" && segment.fontFamily.trim()
    ? segment.fontFamily.trim()
    : undefined;

  if (!hasFontSize && !fontFamily) {
    return font;
  }

  const nextSize = hasFontSize ? fontSize : getFontSize(font);

  if (fontFamily) {
    const prefixMatch = typeof font === "string" ? font.match(/^(.*?)(\d+(?:\.\d+)?px)/i) : null;
    const prefix = prefixMatch ? prefixMatch[1] : "";
    return `${prefix}${nextSize}px ${fontFamily}`;
  }

  if (typeof font === "string" && /(\d+(?:\.\d+)?)px/i.test(font)) {
    return font.replace(/(\d+(?:\.\d+)?)px/i, `${nextSize}px`);
  }

  return `${nextSize}px ${getFontFamily(font)}`;
}

function getSegmentFont(font, segmentOrScript) {
  const segment = typeof segmentOrScript === "string"
    ? { script: segmentOrScript }
    : (segmentOrScript || {});
  const segmentFont = applyFontOverrides(font, segment);

  if (segment.script === "sup" || segment.script === "sub") {
    return scaleFont(segmentFont, SCRIPT_FONT_SCALE);
  }

  return segmentFont;
}

function getSegmentYOffset(fontSize, script) {
  if (script === "sup") {
    return fontSize * SUPERSCRIPT_OFFSET_FACTOR;
  }

  if (script === "sub") {
    return fontSize * SUBSCRIPT_OFFSET_FACTOR;
  }

  return 0;
}

function parseRichLabelText(text) {
  const source = text === undefined || text === null ? "" : String(text);
  const segments = [];
  const scriptStack = [];
  let cursor = 0;

  const appendText = (value) => {
    if (!value) return;

    const script = scriptStack.length > 0 ? scriptStack[scriptStack.length - 1] : "normal";
    const previous = segments[segments.length - 1];

    if (previous && previous.script === script) {
      previous.text += value;
    } else {
      segments.push({ text: value, script });
    }
  };

  source.replace(SCRIPT_TAG_PATTERN, (match, tag, offset) => {
    appendText(source.slice(cursor, offset));

    const normalizedTag = tag.toLowerCase();
    const isClosingTag = match[1] === "/";

    if (isClosingTag) {
      const lastMatchingTagIndex = scriptStack.lastIndexOf(normalizedTag);
      if (lastMatchingTagIndex !== -1) {
        scriptStack.splice(lastMatchingTagIndex, 1);
      }
    } else {
      scriptStack.push(normalizedTag);
    }

    cursor = offset + match.length;
    return match;
  });

  appendText(source.slice(cursor));
  return segments;
}

function getRenderableSegments(textOrSegments) {
  if (Array.isArray(textOrSegments)) {
    return textOrSegments
      .map((segment) => ({
        text: String(segment?.text ?? ""),
        script: segment?.script === "sup" || segment?.script === "sub" ? segment.script : "normal",
        color: typeof segment?.color === "string" && segment.color.trim() ? segment.color.trim() : undefined,
        fontSize: Number(segment?.fontSize) > 0 ? Number(segment.fontSize) : undefined,
        fontFamily: typeof segment?.fontFamily === "string" && segment.fontFamily.trim() ? segment.fontFamily.trim() : undefined,
      }))
      .filter((segment) => segment.text);
  }

  return parseRichLabelText(textOrSegments);
}

function measureRichTextLineMetrics(textOrSegments, font) {
  const fontSize = getFontSize(font);
  const baseAscent = fontSize / 2;
  const baseDescent = fontSize / 2;
  let ascent = baseAscent;
  let descent = baseDescent;

  getRenderableSegments(textOrSegments).forEach((segment) => {
    const segmentFont = getSegmentFont(font, segment);
    const segmentFontSize = getFontSize(segmentFont);
    const segmentOffset = getSegmentYOffset(fontSize, segment.script);

    ascent = Math.max(ascent, segmentFontSize / 2 - segmentOffset);
    descent = Math.max(descent, segmentFontSize / 2 + segmentOffset);
  });

  return {
    ascent,
    descent,
    height: ascent + descent,
  };
}

function measureRichTextLine(ctx, textOrSegments, font) {
  const originalFont = ctx.font;
  const segments = getRenderableSegments(textOrSegments);
  let width = 0;

  segments.forEach((segment) => {
    ctx.font = getSegmentFont(font, segment);
    width += ctx.measureText(segment.text).width;
  });

  ctx.font = originalFont;
  return width;
}

function drawRichTextLine(ctx, textOrSegments, x, y, options = {}) {
  const font = options.font || ctx.font;
  const textAlign = options.textAlign || ctx.textAlign || "left";
  const fillStyle = options.fillStyle || ctx.fillStyle;
  const fontSize = getFontSize(font);
  const width = measureRichTextLine(ctx, textOrSegments, font);
  const originalFont = ctx.font;
  const originalTextAlign = ctx.textAlign;
  const originalFillStyle = ctx.fillStyle;

  let currentX = x;
  if (textAlign === "center") {
    currentX = x - width / 2;
  } else if (textAlign === "right" || textAlign === "end") {
    currentX = x - width;
  }

  ctx.textAlign = "left";
  ctx.fillStyle = fillStyle;

  getRenderableSegments(textOrSegments).forEach((segment) => {
    const segmentFont = getSegmentFont(font, segment);
    ctx.font = segmentFont;
    ctx.fillStyle = segment.color || fillStyle;
    ctx.fillText(segment.text, currentX, y + getSegmentYOffset(fontSize, segment.script));
    currentX += ctx.measureText(segment.text).width;
  });

  ctx.font = originalFont;
  ctx.textAlign = originalTextAlign;
  ctx.fillStyle = originalFillStyle;
}

export {
  drawRichTextLine,
  getFontSize,
  measureRichTextLine,
  measureRichTextLineMetrics,
  parseRichLabelText,
  scaleFont,
};
